import { ColorContext } from 'App';
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import React, { useContext } from 'react';
import { FaSortDown, FaSortUp } from 'react-icons/fa';

const titles = ['Source', 'User', 'Device', 'Activity'];

// Header title - sort/data key
const fixTitleToKey = (title) => {
  if (title === 'Activity') return 'timestamp';
  if (title === 'Id') return 'log_id';
  if (title === 'Source') return 'action_type';
  if (title === 'User') return 'name';
  if (title === 'Device') return 'log_id';
  return title.toLowerCase();
};

// Cell value mapping
const getFieldValue = (item, title) => {
  if (title === 'Activity') return new Date(item.timestamp).toLocaleString();
  if (title === 'Id') return item.log_id;
  if (title === 'Source') return item.action_type;
  if (title === 'User') return item.user_name;
  if (title === 'Device') return item.additional_info?.device || ' ';
  return item[title.toLowerCase()];
};

const AuditLogDisplay = ({
  list,
  selectedIndex,
  setSelectedIndex,
  sortKey,
  setSortKey,
  sortOrder,
  setSortOrder,
}) => {
  const theme = useContext(ColorContext);

  const changeSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortOrder(1);
    }
  };

  if (!list) return <WidgetLoadingAnimation />;
  if (list.length === 0) return <WidgetLoadingAnimation />;

  // Build rows for BaseTable, ensure an `id` for selection
  const rows = list.map((item, index) => ({
    ...item,
    id: item.log_id ?? index,
  }));

  const selectedRowId =
    selectedIndex >= 0 && selectedIndex < list.length
      ? (list[selectedIndex].log_id ?? selectedIndex)
      : null;

  // Columns for BaseTable / MaterialReactTable
  const columns = titles.map((title) => {
    const id = fixTitleToKey(title);
    return {
      id,
      Header: () => (
        <CategoryTitle
          title={title}
          changeSort={changeSort}
          sortKey={sortKey}
          sortOrder={sortOrder}
        />
      ),
      accessorFn: (row) => getFieldValue(row, title),
      enableSorting: false, // sorting handled externally
    };
  });

  const onRowClick = (row) => {
    const rowId = row.log_id ?? row.id;
    const idx = list.findIndex((item, index) => (item.log_id ?? index) === rowId);

    if (idx === -1) return;

    if (idx === selectedIndex) setSelectedIndex(-1);
    else setSelectedIndex(idx);
  };

  return (
    <div className="h-100 d-flex flex-column">
      <BaseTable
        columns={columns}
        rows={rows}
        expandable
        onRowClick={onRowClick}
        selectedRowId={selectedRowId}
        altRowSettings={{
          alternate: true,
          color1: '#222a2d',
          color2: '#2C373B',
        }}
        customStyles={{
          container: { height: '100%' },
          header: { color: '#ffffff' },
          cell: {
            color: '#ffffff',
            borderBottom: '1px solid #56577A',
            cursor: 'pointer',
            fontSize: '16px',
          },
        }}
        muiTablePaperProps={{
          sx: { height: '100%', display: 'flex', flexDirection: 'column' },
        }}
        muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto' } }}
        dropdownMenu={(row) => (
          <div className="w-100 d-flex flex-column user-select-text">
            <div className="p-2 border-top border-1 border-light-subtle">{row.description}</div>
          </div>
        )}
      />
    </div>
  );
};

const CategoryTitle = ({ title, changeSort, sortKey, sortOrder }) => {
  const key = fixTitleToKey(title);

  return (
    <div
      onClick={() => changeSort(key)}
      style={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <span>{title}</span>
      {sortKey === key && (
        <span className="ms-1">{sortOrder === 1 ? <FaSortDown /> : <FaSortUp />}</span>
      )}
    </div>
  );
};

export default AuditLogDisplay;
